/**
 * Build a Datadog-friendly release version tag from a git ref and commit SHA.
 *
 * Shared by tracing.ts and logger.ts (reading the NEXT_PUBLIC_RELEASE_* values
 * Next.js inlines into the browser bundle) and by postbuild.ts (reading raw
 * VERCEL_GIT_COMMIT_* / GITHUB_* env vars in Node before uploading source maps).
 * The runtime RUM tag and the source-map release-version must match exactly
 * for Datadog stack-trace resolution, which is why there is a single source
 * of truth here.
 *
 * @param ref Git ref name. Examples: "release/1.7.0", "main",
 *   "feat/SCR-4121-foo". Empty or undefined produces "dev".
 * @param sha Git commit SHA. Empty or undefined produces "local". Truncated
 *   to 7 chars to match `git rev-parse --short`.
 * @returns Sanitised version tag. Examples: "1.7.0-a5d41b7", "main-1234abc",
 *   "feat-SCR-4121-foo-9b2c0d1", "dev-local".
 */
export function formatVersion(ref: string | undefined, sha: string | undefined): string {
  const refStr = ref || '';
  const release = refStr.startsWith('release/')
    ? refStr.slice('release/'.length)
    : refStr || 'dev';
  const shaStr = (sha || 'local').slice(0, 7);
  return `${release}-${shaStr}`.replace(/[^a-zA-Z0-9._-]/g, '-');
}

/** Plain release semver such as `1.104.0`; only `toAppSemver` produces it. */
export type AppSemver = string & { readonly __brand: 'AppSemver' };

const APP_SEMVER = /^\d+(\.\d+){2,3}(-[0-9A-Za-z.]+)?$/;

export function toAppSemver(value: string | undefined): AppSemver | undefined {
  return value && APP_SEMVER.test(value) ? (value as AppSemver) : undefined;
}

export function releaseSemver(ref: string | undefined): AppSemver | undefined {
  if (!ref?.startsWith('release/')) return undefined;

  return toAppSemver(ref.slice('release/'.length));
}
